import { useState } from "react";
import { confirmDialog } from "../components/ConfirmDialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Save, Trash2, Lightbulb } from "lucide-react";

const STATUS_META = {
  new: { label: "Nouvelle", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  review: { label: "À l'étude", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30" },
  planned: { label: "Planifiée", cls: "bg-violet-500/10 text-violet-300 border-violet-500/30" },
  done: { label: "Réalisée", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  rejected: { label: "Non retenue", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
};

const STATUS_OPTIONS = [
  { value: "new", label: "Nouvelle" },
  { value: "review", label: "À l'étude" },
  { value: "planned", label: "Planifiée" },
  { value: "done", label: "Réalisée" },
  { value: "rejected", label: "Non retenue" },
];

const TIER_META = {
  free: { label: "Gratuit", cls: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30" },
  essentiel: { label: "Essentiel", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  pro: { label: "Pro", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30" },
  max: { label: "Max", cls: "bg-violet-500/10 text-violet-300 border-violet-500/30" },
};

const CATEGORIES = [
  { value: "portefeuilles", label: "Portefeuilles" },
  { value: "analyses", label: "Analyses" },
  { value: "cours", label: "Cours" },
  { value: "outils", label: "Outils" },
  { value: "site", label: "Site / compte" },
  { value: "autre", label: "Autre" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const selectCls =
  "rounded-md bg-white/5 border border-white/10 px-2.5 h-9 text-sm text-zinc-100 focus:outline-none focus:border-emerald-500/50";

function CounterCard({ label, value, cls }) {
  return (
    <Card className={`p-4 bg-card border ${cls}`}>
      <div className="text-2xl font-display font-black tabular-nums">{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.15em] text-zinc-400">{label}</div>
    </Card>
  );
}

function IdeaCard({ idea, onSave, onDelete, saving }) {
  const [status, setStatus] = useState(idea.status || "new");
  const [reply, setReply] = useState(idea.admin_reply || "");
  const meta = STATUS_META[idea.status] || STATUS_META.new;
  const tier = TIER_META[idea.user_tier] || TIER_META.free;
  return (
    <Card className="p-4 bg-card border-white/5" data-testid={`admin-idea-${idea.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-zinc-100">{idea.title}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.15em] ${meta.cls}`}>
              {meta.label}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.03] text-[10px] uppercase tracking-[0.12em] text-zinc-300">
              {CAT_LABEL[idea.category] || idea.category}
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.15em] ${tier.cls}`}
              title="Abonnement de l'auteur"
            >
              {tier.label}
            </span>
          </div>
          <p className="mt-2 text-sm text-zinc-300 whitespace-pre-line">{idea.description}</p>
          <div className="mt-2 text-[11px] text-zinc-500">
            {idea.email} · {idea.created_at ? new Date(idea.created_at).toLocaleString("fr-FR") : ""}
          </div>
          {idea.admin_reply && (
            <blockquote className="mt-3 border-l-2 border-violet-500/50 pl-3 text-sm text-zinc-300">
              <span className="text-violet-300 font-medium">{idea.replied_by || "Side B"}</span>
              <span className="text-zinc-500"> · </span>
              {idea.admin_reply}
            </blockquote>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={`${selectCls} w-full sm:w-auto`}
          data-testid={`admin-idea-status-${idea.id}`}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} className="bg-[#0F0F10]">
              {o.label}
            </option>
          ))}
        </select>
        <input
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          maxLength={500}
          placeholder="Réponse visible par l'abonné (optionnel)"
          className="flex-1 min-w-[12rem] rounded-md bg-white/5 border border-white/10 px-3 h-9 text-sm text-zinc-100 focus:outline-none focus:border-emerald-500/50"
          data-testid={`admin-idea-reply-${idea.id}`}
        />
        <Button
          size="sm"
          className="bg-emerald-500 text-black hover:bg-emerald-300"
          disabled={saving}
          onClick={() => onSave(idea.id, { status, admin_reply: reply })}
          data-testid={`admin-idea-save-${idea.id}`}
        >
          <Save className="w-4 h-4 mr-1" /> Enregistrer
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-rose-400 hover:bg-rose-500/10"
          onClick={async () => (await confirmDialog({ title: "Supprimer définitivement cette idée ?", description: "L'idée et la réponse éventuelle disparaissent. Action définitive." })) && onDelete(idea.id)}
          data-testid={`admin-idea-delete-${idea.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}

export default function AdminIdeasPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("new");
  const [category, setCategory] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-ideas"],
    queryFn: async () => (await api.get("/admin/ideas")).data,
  });
  const ideas = data?.ideas || [];
  const counts = data?.counts || {};

  const modMut = useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/admin/ideas/${id}`, payload)).data,
    onSuccess: () => {
      toast.success("Idée mise à jour · l'abonné est notifié");
      qc.invalidateQueries({ queryKey: ["admin-ideas"] });
      qc.invalidateQueries({ queryKey: ["my-ideas"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/ideas/${id}`)).data,
    onSuccess: () => {
      toast.success("Supprimé");
      qc.invalidateQueries({ queryKey: ["admin-ideas"] });
      qc.invalidateQueries({ queryKey: ["my-ideas"] });
    },
  });

  const filtered = ideas
    .filter((i) => filter === "all" || i.status === filter)
    .filter((i) => category === "all" || i.category === category);

  const FILTERS = [
    { id: "new", label: `Nouvelles (${counts.new || 0})` },
    { id: "review", label: `À l'étude (${counts.review || 0})` },
    { id: "planned", label: `Planifiées (${counts.planned || 0})` },
    { id: "done", label: `Réalisées (${counts.done || 0})` },
    { id: "rejected", label: `Refusées (${counts.rejected || 0})` },
    { id: "all", label: `Toutes (${ideas.length})` },
  ];

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Admin</div>
        <h1 className="font-display font-black text-3xl">Idées des abonnés</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Les idées envoyées par les abonnés Essentiel, Pro et Max. Changez leur statut et répondez : l'abonné est notifié
          et voit votre réponse dans son espace.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <CounterCard label="Nouvelles" value={counts.new || 0} cls="border-amber-500/30" />
        <CounterCard label="À l'étude" value={counts.review || 0} cls="border-sky-500/30" />
        <CounterCard label="Planifiées" value={counts.planned || 0} cls="border-violet-500/30" />
        <CounterCard label="Réalisées" value={counts.done || 0} cls="border-emerald-500/30" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-ideas-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`admin-ideas-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.15em] border transition-colors ${
              filter === f.id
                ? "bg-emerald-500 text-black border-emerald-500"
                : "border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 text-xs text-zinc-400">
          <span>Catégorie :</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls} data-testid="admin-ideas-category">
            <option value="all" className="bg-[#0F0F10]">Toutes</option>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value} className="bg-[#0F0F10]">{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center bg-card border-white/5">
          <Lightbulb className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">
            {filter === "new" && category === "all"
              ? "Aucune nouvelle idée. Tout est traité."
              : "Aucune idée dans cette catégorie."}
          </p>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="admin-ideas-list">
          {filtered.map((idea) => (
            <IdeaCard
              key={idea.id}
              idea={idea}
              saving={modMut.isPending}
              onSave={(id, payload) => modMut.mutate({ id, payload })}
              onDelete={(id) => delMut.mutate(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
